import { CodeBracketIcon } from '@heroicons/react/24/outline';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { VariablePicker } from './VariablePicker';
import type { VariableSection } from './variableSections';

interface TokenFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  sections: VariableSection[];
  transforms: string[];
  placeholder?: string;
  errors?: string[];
  children?: React.ReactNode;
}

/**
 * A text input whose companion button opens the variable picker. Insertion lands
 * at the caret rather than at the end, so a token can be dropped into the middle
 * of a sentence — the legacy behaviour, and the reason the input is owned here.
 */
export function TokenField({
  label,
  value,
  onChange,
  sections,
  transforms,
  placeholder,
  errors = [],
  children
}: TokenFieldProps) {
  const { t } = useTranslation();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number | null>(null);
  const [open, setOpen] = useState(false);

  // Runs once the picker has closed: closing a <dialog> restores focus to whatever
  // opened it, so putting the caret back any earlier is immediately undone.
  useEffect(() => {
    if (open) return;
    const caret = caretRef.current;
    if (caret === null || !inputRef.current) return;
    caretRef.current = null;
    inputRef.current.focus();
    inputRef.current.setSelectionRange(caret, caret);
  }, [open]);

  const insert = (token: string) => {
    const input = inputRef.current;
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    caretRef.current = start + token.length;
    onChange(value.slice(0, start) + token + value.slice(end));
  };

  return (
    <div className="text-sm">
      <label htmlFor={inputId} className="font-medium text-foreground">
        {label}
      </label>

      <div className="mt-1 flex gap-2">
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-sm border border-input bg-surface px-3 py-2 font-mono text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={sections.length === 0}
          className="inline-flex shrink-0 items-center gap-1.5 rounded border border-input bg-surface px-2.5 py-2 text-xs font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-40"
        >
          <CodeBracketIcon className="size-4" aria-hidden="true" />
          {t('variables.insert')}
        </button>
      </div>

      {errors.map((error) => (
        <span key={error} className="mt-1 block text-xs text-destructive">
          {error}
        </span>
      ))}

      {children}

      <VariablePicker
        open={open}
        sections={sections}
        transforms={transforms}
        onInsert={insert}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}
